import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getSession } from '../services/api';
import apiClient from '../services/apiClient';
import styles from './MagicLoginRedirect.module.css';

export const MagicLoginRedirect: React.FC<{ onLogin?: (user: any) => void }> = ({ onLogin }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState<'init' | 'verifying' | 'checking' | 'ready'>('init');
  const [needsVerification, setNeedsVerification] = useState(false); // Zeigt an, ob Verifizierung erforderlich ist
  const [isInactive, setIsInactive] = useState(false); // Zeigt an, ob Account inaktiv ist
  const hasRun = useRef(false); // Verhindert doppelte Ausführung

  useEffect(() => {
    // Verhindere doppelte Ausführung in React StrictMode
    if (hasRun.current) return;
    hasRun.current = true;

    const params = new URLSearchParams(location.search);
    const token = params.get('token');

    if (!token) {
      setError('No token found');
      setLoading(false);
      return;
    }

    const doVerification = async () => {
      try {
        setStage('verifying');
        console.log('🔐 Starting magic link verification...');
        
        // Token verifizieren - Backend setzt Cookie
        let verifyResponse;
        try {
          verifyResponse = await apiClient.get(`/api/v2/web/auth/magic-link/verify?token=${token}`, {
            maxRedirects: 0,
            validateStatus: () => true, // Akzeptiere alle Status-Codes
            headers: {
              'X-Skip-Interceptor': 'true'
            }
          });
          console.log('✅ Magic link verification response:', verifyResponse.status, verifyResponse.data);
          
          // Prüfe auf Fehler-Status-Codes
          if (verifyResponse.status === 403 || verifyResponse.status === 401) {
            // Fehler vom Backend - werfe Exception für Fehlerbehandlung
            const errorData = verifyResponse.data;
            const error: any = new Error(errorData?.detail || 'Authentication failed');
            error.response = verifyResponse;
            throw error;
          } else if (verifyResponse.status >= 400) {
            // Andere Fehler
            const errorData = verifyResponse.data;
            const error: any = new Error(errorData?.detail || `Server error (${verifyResponse.status})`);
            error.response = verifyResponse;
            throw error;
          }
        } catch (verifyError: any) {
          // Wenn es ein echter Fehler ist (nicht nur 302), werfen wir ihn weiter
          if (verifyError.response) {
            // HTTP-Fehler mit Response
            throw verifyError;
          }
          // Sonst: 302 Redirect oder Network Error ist OK - Cookie wurde gesetzt
          console.log('✅ Magic link request completed (302 expected)');
        }
        
        // Kurze Pause für Cookie-Verarbeitung
        await new Promise(resolve => setTimeout(resolve, 300));
        
        setStage('checking');
        console.log('🔍 Checking session status...');
        
        // Session-Status abfragen
        const sessionData = await getSession();
        console.log('📋 Session data received:', sessionData);
        
        if (sessionData.authenticated && sessionData.user) {
          console.log('✅ Authentication successful, user:', sessionData.user.mail);
          
          // Benutzer im App-State aktualisieren
          if (onLogin) {
            onLogin(sessionData.user);
          }
          
          // Ziel-URL ermitteln
          let targetUrl = '/events'; // Default
          try {
            const tokenParts = token.split('.');
            const decodedData = atob(tokenParts[0]);
            const tokenData = JSON.parse(decodedData);
            const forward_url = tokenData.forward_url;
            
            if (forward_url && forward_url !== '/' && forward_url !== '') {
              targetUrl = forward_url;
            }
            console.log('🎯 Target URL:', targetUrl);
          } catch (e) {
            console.warn('⚠️ Could not parse token, using default /events');
          }
          
          setStage('ready');
          
          // Warten bis UI aktualisiert ist, dann einmalige Navigation
          setTimeout(() => {
            console.log('🚀 Navigating to:', targetUrl);
            // Verwende navigate statt window.location.href um Neuladen zu vermeiden
            navigate(targetUrl, { replace: true });
          }, 800);
        } else {
          throw new Error('Authentication failed');
        }
      } catch (err: any) {
        console.error('Magic link error:', err);
        
        // Granulare Fehlerbehandlung
        let errorMessage = 'Unknown error occurred';
        
        if (err.response) {
          // Server hat mit einem Fehlercode geantwortet
          const status = err.response.status;
          const data = err.response.data;
          
          if (status === 401 || status === 403) {
            // Spezielle Behandlung für Verifizierungs- und Account-Probleme
            if (data?.detail?.includes('verification') || data?.detail?.includes('verified')) {
              errorMessage = data.detail || 'Your account needs to be verified. Please check your email for a verification link.';
              setNeedsVerification(true); // Markiere als Verifizierungsproblem
            } else if (data?.detail?.includes('inactive')) {
              errorMessage = data.detail || 'Your account is inactive. Please contact support.';
              setIsInactive(true); // Markiere als inaktiv
            } else {
              errorMessage = 'Invalid or expired magic link. Please request a new one.';
            }
          } else if (status === 404) {
            errorMessage = 'Authentication endpoint not found. Please contact support.';
          } else if (status === 500) {
            errorMessage = data?.detail || 'Server error occurred. Please try again later.';
          } else if (data?.detail) {
            errorMessage = data.detail;
          } else {
            errorMessage = `Server error (${status}). Please try again.`;
          }
        } else if (err.request) {
          // Request wurde gesendet, aber keine Antwort erhalten
          errorMessage = 'No response from server. Please check your internet connection.';
        } else if (err.message) {
          // Fehler beim Setup des Requests
          if (err.message.includes('Network Error')) {
            errorMessage = 'Network error. Please check your internet connection.';
          } else if (err.message.includes('timeout')) {
            errorMessage = 'Request timeout. Please try again.';
          } else {
            errorMessage = err.message;
          }
        }
        
        setError(errorMessage);
        setLoading(false);
      }
    };

    doVerification();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Nur einmal beim Mount ausführen

  if (loading) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.container}>
          <div className={styles.icon}>
            {stage === 'init' && '🔐'}
            {stage === 'verifying' && '🔍'}
            {stage === 'checking' && '✅'}
            {stage === 'ready' && '🎉'}
          </div>
          <h2 className={styles.title}>
            Authentication in progress...
          </h2>
          <p className={styles.description}>
            {stage === 'init' && 'Initializing...'}
            {stage === 'verifying' && 'Verifying your magic link...'}
            {stage === 'checking' && 'Checking your login status...'}
            {stage === 'ready' && 'Success! Redirecting you now...'}
          </p>
          <div className={styles.spinner}></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.errorContainer}>
          <div className={styles.errorIcon}>
            {needsVerification ? '📧' : isInactive ? '🚫' : '❌'}
          </div>
          <h2 className={styles.errorTitle} style={{ 
            color: needsVerification ? '#f39c12' : isInactive ? '#e74c3c' : '#e74c3c'
          }}>
            {needsVerification ? 'Verification Required' : isInactive ? 'Account Inactive' : 'Login Error'}
          </h2>
          
          {needsVerification ? (
            <>
              <div className={styles.errorContent}>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Your account needs to be verified before you can log in.</strong>
                </p>
                <p className={styles.warningBox}>
                  📨 A verification email has been sent to your email address.
                </p>
                <p style={{ marginBottom: '0.5rem', textAlign: 'left' }}>
                  <strong>Next steps:</strong>
                </p>
                <ol className={styles.stepsList}>
                  <li>Check your email inbox (and spam folder)</li>
                  <li>Click the verification link in the email</li>
                  <li>Return here and request a new magic link to log in</li>
                </ol>
              </div>
              <button 
                onClick={() => navigate('/login', { replace: true })}
                className="btn btn-primary"
                style={{ width: '100%', maxWidth: '300px' }}
              >
                Back to Login
              </button>
            </>
          ) : isInactive ? (
            <>
              <div className={styles.errorContent}>
                <p className={styles.infoBoxGray}>
                  📧 Please contact support at <strong>events@ypog.law</strong> for assistance.
                </p>
              </div>
              <button 
                onClick={() => navigate('/login', { replace: true })}
                className="btn btn-primary"
                style={{ width: '100%', maxWidth: '300px' }}
              >
                Back to Login
              </button>
            </>
          ) : (
            <>
              <p className={styles.errorMessage}>
                {error}
              </p>
              <button 
                onClick={() => navigate('/login', { replace: true })}
                className="btn btn-primary"
                style={{ width: '100%', maxWidth: '300px' }}
              >
                Back to Login
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.redirectContainer}>
        <div className={styles.redirectIcon}>🚀</div>
        Redirecting...
      </div>
    </div>
  );
};
